import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import BaseFilters from "@/components/v2/molecules/admin/shared/BaseFilters";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { useMemo } from "react";
import { filterActiveContracts } from "@/utils/contractUtils";

interface BusinessExpenseFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedOrganizations: string[];
  setSelectedOrganizations: (value: string[]) => void;
  selectedProperties: string[];
  setSelectedProperties: (value: string[]) => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  typeOptions: { value: string; label: string }[];
  hasActiveFilters: boolean;
  clearFilters: () => void;
  canClickPastDates: boolean;
}

const BusinessExpenseFilters = ({
  searchTerm,
  setSearchTerm,
  selectedOrganizations,
  setSelectedOrganizations,
  selectedProperties,
  setSelectedProperties,
  selectedType,
  setSelectedType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  typeOptions,
  hasActiveFilters,
  clearFilters,
}: BusinessExpenseFiltersProps) => {
  const { data: allProperties = [] } = useProperties();
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  // Filter properties by selected organizations
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      const activePropertyIds = new Set(
        filterActiveContracts(contracts).map(c => c.property_id)
      );
      return allProperties.filter(p => activePropertyIds.has(p.id));
    }

    const activeContracts = filterActiveContracts(contracts).filter(
      (contract) =>
        selectedOrganizations.includes(contract.organization_id || "")
    );

    const propertyIds = activeContracts.map((contract) => contract.property_id);
    return allProperties.filter((property) => propertyIds.includes(property.id));
  }, [selectedOrganizations, allProperties, contracts]);

  const handleDateRangeChange = (from: Date | undefined, to: Date | undefined) => {
    setStartDate(from);
    setEndDate(to);
  };

  return (
    <BaseFilters
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Buscar por descripción..."
      dateFrom={startDate}
      dateTo={endDate}
      onDateRangeChange={handleDateRangeChange}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={clearFilters}
    >
      <OrganizationsMultiSelect
        organizations={organizations}
        selectedOrganizations={selectedOrganizations}
        onSelectionChange={setSelectedOrganizations}
      />

      <PropertiesMultiSelect
        properties={filteredProperties}
        selectedProperties={selectedProperties}
        onSelectionChange={setSelectedProperties}
      />

      <Select value={selectedType} onValueChange={setSelectedType}>
        <SelectTrigger className="rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 h-10 w-[180px]">
          <SelectValue placeholder="Todos los tipos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos los tipos</SelectItem>
          {typeOptions.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </BaseFilters>
  );
};

export default BusinessExpenseFilters;

